import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  TrophyIcon
} from '@heroicons/react/24/outline'
import { CreateKazyCupWinnerData } from '../services/kazyCupApi'
import PhotoUpload from './PhotoUpload'

interface CreateKazyCupWinnerModalProps {
  isOpen: boolean
  onClose: () => void
  onCreateWinner: (winnerData: CreateKazyCupWinnerData) => Promise<void>
  isLoading?: boolean
}

export default function CreateKazyCupWinnerModal({
  isOpen,
  onClose,
  onCreateWinner,
  isLoading = false
}: CreateKazyCupWinnerModalProps) {
  const [formData, setFormData] = useState<CreateKazyCupWinnerData>({
    year: new Date().getFullYear(),
    winner: '',
    achievement: '',
    description: '',
    photo: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.winner || !formData.achievement || !formData.description) {
      return
    }

    try {
      await onCreateWinner(formData)
      
      // Reset form
      setFormData({
        year: new Date().getFullYear(),
        winner: '',
        achievement: '',
        description: '',
        photo: ''
      })
    } catch (error) {
      // Error handling is done in parent component
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-yellow-500 rounded-lg">
                  <TrophyIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Add Kazy Cup Winner
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Year and Winner Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Year *
                  </label>
                  <input
                    type="number"
                    min="2008"
                    max={new Date().getFullYear() + 1}
                    value={formData.year}
                    onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Winner Name *
                  </label>
                  <input
                    type="text"
                    value={formData.winner}
                    onChange={(e) => setFormData(prev => ({ ...prev, winner: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter winner's name"
                    required
                  />
                </div>
              </div>

              {/* Achievement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Achievement Category *
                </label>
                <input
                  type="text"
                  value={formData.achievement}
                  onChange={(e) => setFormData(prev => ({ ...prev, achievement: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Community Leadership, Innovation, Mentorship, etc."
                  required
                />
              </div>

              {/* Photo Upload */}
              <PhotoUpload
                currentPhotoUrl={formData.photo}
                onPhotoUploaded={(photoUrl) => setFormData(prev => ({ ...prev, photo: photoUrl }))}
                onPhotoRemoved={() => setFormData(prev => ({ ...prev, photo: '' }))}
              />

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Describe their outstanding contributions and why they deserve this recognition..."
                  required
                />
              </div>

              {/* Award Info */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <TrophyIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Kazy Cup Recognition
                    </h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      This award recognizes exceptional members who embody the spirit of the Midwest Railbirds 
                      through leadership, community involvement, and dedication to our Derby traditions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !formData.winner || !formData.achievement || !formData.description}
                  className="px-4 py-2 text-sm font-medium text-rb-dark bg-rb-gold border border-transparent rounded-lg hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rb-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Adding Winner...' : 'Add Winner'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  )
}
